/**
 * GoogleReviews context — EN sanningskälla för Google-rating/omdömen
 * på hela sajten. Hämtas en gång vid app-mount och delas via context.
 *
 * Data kommer från vår server-side proxy /api/reviews/google
 * (server cachar mot Google i ~12h, se backend/google_places.py).
 * API-nyckeln ligger enbart server-side.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

/** Konfigpunkt: bara visa recensioner med minst denna rating. */
export const MIN_DISPLAYED_REVIEW_RATING = 5;

const GoogleReviewsContext = createContext(null);

const EMPTY = {
  rating: 0,
  user_rating_count: 0,
  google_maps_uri: "",
  display_name: "",
  reviews: [],
  source: "unavailable",
};

export function GoogleReviewsProvider({ children }) {
  const [data, setData] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/reviews/google");
        if (!cancelled) setData(r.data || EMPTY);
      } catch {
        if (!cancelled) setData(EMPTY);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const filtered = (data.reviews || []).filter(
      (r) => Number(r.rating || 0) >= MIN_DISPLAYED_REVIEW_RATING,
    );
    // Räkning tillgänglig som live data (endast om vi faktiskt fick data)
    const hasData =
      data.source !== "unavailable" && Number(data.user_rating_count) > 0;
    return {
      loaded,
      hasData,
      rating: Number(data.rating || 0),
      ratingCount: Number(data.user_rating_count || 0),
      googleMapsUri: data.google_maps_uri || "",
      displayName: data.display_name || "",
      /** 5-stjärniga recensioner (redan filtrerade). */
      reviews: filtered,
      source: data.source,
    };
  }, [data, loaded]);

  return (
    <GoogleReviewsContext.Provider value={value}>
      {children}
    </GoogleReviewsContext.Provider>
  );
}

export function useGoogleReviews() {
  const ctx = useContext(GoogleReviewsContext);
  if (ctx) return ctx;
  // Fallback (t.ex. under react-snap prerender) — safe defaults, ingen krasch.
  return {
    loaded: false,
    hasData: false,
    rating: 0,
    ratingCount: 0,
    googleMapsUri: "",
    displayName: "",
    reviews: [],
    source: "unavailable",
  };
}

/** Formatera 5.0 som "5,0" för svensk publik. */
export function formatRatingSv(rating) {
  if (!rating) return "";
  // Om det är ett jämnt heltal (5.0) -> "5"; annars komma-decimal.
  const rounded = Math.round(rating * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(1).replace(".", ",");
}

/** Formatera antal recensioner med tusentalstecken enligt sv-SE. */
export function formatCountSv(n) {
  if (!n) return "0";
  try {
    return new Intl.NumberFormat("sv-SE").format(n);
  } catch {
    return String(n);
  }
}
